import { ApiResponse } from "../api";
import { apiQueryAdapter } from "../apiQueryAdapter";
import { EquipmentFormModel } from "./models";
import { PageResponseModel } from "../common/models";

class EquipmentService {
  private static instance: EquipmentService;

  private constructor() {}

  static getInstance(): EquipmentService {
    if (!EquipmentService.instance) {
      EquipmentService.instance = new EquipmentService();
    }
    return EquipmentService.instance;
  }

  public getEquipmentTypes = async () => {
    const response = await apiQueryAdapter.query("/equipments/equipment-types", "GET");
    return response.data;
  };

  public getProductLines = async () => {
    const response = await apiQueryAdapter.query("/equipments/product-lines", "GET");
    return response.data;
  };

  public getAssetDescriptor = async () => {
    const response = await apiQueryAdapter.query("/equipments/asset-descriptors", "GET");
    return response.data;
  };

  public getLeaseTypes = async () => {
    const response = await apiQueryAdapter.query("/equipments/lease-types", "GET");
    return response.data;
  };

  public getMakes = async () => {
    const response = await apiQueryAdapter.query("/equipments/makes", "GET");
    return response.data;
  };

  public getEquipmentList = async (
    search?: string,
    page?: number,
    type?: string,
    status?: string,
    location?: string
  ): Promise<PageResponseModel<EquipmentFormModel>> => {
    
    const params = new URLSearchParams();

    if (search) params.set("search", search);
    if (type) params.set("type", type);
    if (status) params.set("status", status);
    if (location) params.set("location", location);
    if (page !== undefined) params.set("page", page.toString());

    const response = await apiQueryAdapter.query("/equipments", "GET", { params });

    return response.data;
  };


  public getEquipmentDetails = async (
    equipmentNumber: string,
    page?: number
  ): Promise<PageResponseModel<EquipmentFormModel>> => {

    const params = new URLSearchParams();
    if (page !== undefined) params.set("page", page.toString());

    const response = await apiQueryAdapter.query(
      `/equipments/equipment/${equipmentNumber}`,
      "GET",
      { params }
    );

    return response.data;
  };


  public createEquipment = async (
    data: EquipmentFormModel
  ): Promise<ApiResponse<EquipmentFormModel>> => {

    if (!data) throw new Error("Data is required");

    const response = await apiQueryAdapter.query<ApiResponse<EquipmentFormModel>>(
      `/equipments`,
      "POST",
      { body: data }
    );

    return response.data;
  };

  public updateEquipment = async (
    id: string,
    data: EquipmentFormModel
  ): Promise<ApiResponse<EquipmentFormModel>> => {

    if (!id) throw new Error("Equipment ID is required");
    if (!data) throw new Error("Data is required");

    const response = await apiQueryAdapter.query<ApiResponse<EquipmentFormModel>>(
      `/equipments/equipment/${id}`,
      "PUT",
      { body: data }
    );

    return response.data;
  };

  public deleteEquipment = async (id: string): Promise<ApiResponse<any>> => {
    if (!id) throw new Error("Equipment ID is required");

    const response = await apiQueryAdapter.query(
      `/equipments/equipment/${id}`,
      "DELETE"
    );

    return response;
  };


  public getDownloadEquipmentFile = async (
    fileType: string,
    equipmentId: string
  ): Promise<Blob> => {
    const response = await apiQueryAdapter.query(
      `/equipments/documents/${equipmentId}/${fileType.toUpperCase()}`,
      "GETDOC",
      { responseType: "blob" }
    );

    return response.data;
  };

  public uploadInitialDocument = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await apiQueryAdapter.query(
      `/equipments/documents`,
      "POST",
      { body: formData }
    );

    return response.data;
  };

  public uploadEquipmentFile = async (
    equipmentId: string,
    fileType: string,
    file: File
  ) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await apiQueryAdapter.query(
      `/equipments/documents/${equipmentId}/${fileType.toUpperCase()}`,
      "PUT",
      { body: formData }
    );

    return response.data;
  };

  public deleteEquipmentFile = async (
    equipmentId: string,
    fileType: string
  ): Promise<ApiResponse<any>> => {

    const response = await apiQueryAdapter.query(
      `/equipments/documents/${equipmentId}/${fileType.toUpperCase()}`,
      "DELETE"
    );

    return response;
  };

}

export default EquipmentService;